import React, { useCallback, useMemo } from 'react'
import { Row, Col } from 'reactstrap'

import ActivityHeatmap from './charts/ActivityHeatmap'
import ChartCard from './charts/ChartCard'
import DataTable from './charts/DataTable'
import HorizontalStackedBar from './charts/HorizontalStackedBar'
import Leaderboard from './charts/Leaderboard'
import StatTile, { StatTileRow } from './charts/StatTile'
import TrendLine from './charts/TrendLine'
import {
    buildActionMix,
    buildActionTrend,
    buildDirectionTotals,
    buildExtraTileValue,
    buildHeatmap,
    buildKpis,
    buildLeaderboard,
    buildLeaderboardTable,
    buildMixTableRows,
    buildScopeKpis,
    buildScopeTrend,
    buildUserKpis,
} from './activityReport'
import {
    actionMetaFor,
    entityColorFor,
    extraTilesFor,
    fillPeriod,
    headlineActionsFor,
    scopeChartsFor,
    scopeTabFor,
    seriesFor,
    seriesForChart,
    selfScopedChartsFor,
    selfScopedUserChartsFor,
} from './reportRegistry'

/**
 * Renders whatever charts a report's registry entry declares.
 *
 * This is the whole reason there is no per-report component: the three tab
 * components this replaces differed only in which actions they charted and what
 * they called them, both of which are now data.
 *
 * `layout` picks which of a report's three chart layouts to render:
 * `'module'` (the default) is the report's own subject -- incidents, claims,
 * whatever `report.charts` declares. `'users'` is who did the work --
 * `report.userCharts`, the same generic leaderboard/trend/heatmap layout on
 * every report. `'scope'` is the optional By-<scope> tab (e.g. boards' "By
 * Board") -- the same three chart types keyed by `row.scopeNames` instead of
 * the person, declared per report via `scopeChartsFor` in reportRegistry.js.
 * This is independent of `view`, which is chart-vs-table, not
 * module-vs-users-vs-scope.
 *
 * Colour separates created from edited throughout -- two measures of the same
 * record, and the only comparison on these charts that means anything.
 */

// Stand-ins for `scopeLeaderboard`/`scopeKpis` on a report with no scope tab
// (every report but `boards`, currently) -- see the two memos below. Module
// constants, not inline object literals, so they are referentially stable
// across renders and never fed to `buildLeaderboardTable` in the first
// place (see `scopeLeaderboardTable`, further down).
const EMPTY_LEADERBOARD = { data: [], series: [] }
const EMPTY_SCOPE_KPIS = { activeCount: 0, topName: '--', topCount: 0 }

const ReportBody = ({
    report,
    rows,
    view,
    layout = 'module',
    startDate,
    endDate,
    canViewAllUsers,
    granularity = 'day',
    onInvestigate,
}) => {
    const actionMeta = useMemo(() => actionMetaFor(report), [report])
    // The inbound/outbound split shown on the "Messages created" tile below
    // (inbox only -- see `report.hasDirection`). Built from the same `rows`
    // every other tile on this row already uses, so it obeys every filter
    // exactly like they do; see `buildDirectionTotals`'s own docstring in
    // activityReport.js for why it unions ids rather than summing events, and
    // why it counts only created-mode message rows.
    const directionTotals = useMemo(
        () => (report.hasDirection ? buildDirectionTotals(rows, actionMeta, report.directionEntity) : null),
        [report, rows, actionMeta]
    )

    // A viewer restricted to their own rows gets a layout that suits one
    // person: a one-bar "by staff member" chart, a one-row heatmap, and a
    // one-row leaderboard all say nothing. Derived from the report's own
    // charts (see `selfScopedChartsFor` / `selfScopedUserChartsFor`) so a
    // report that scopes its charts to avoid mixing record types keeps that
    // scoping in the self-scoped layout too.
    const charts = useMemo(() => {
        // Unlike the module and Users layouts, the scope layout does not
        // narrow for a self-scoped viewer -- there is no "compares people"
        // charge to answer here, since none of its three charts are keyed by
        // person (see the brief: a self-scoped viewer still gets all three).
        if (layout === 'scope') return scopeChartsFor(report)
        if (layout === 'users') {
            return canViewAllUsers ? report.userCharts : selfScopedUserChartsFor(report)
        }
        return canViewAllUsers ? report.charts : selfScopedChartsFor(report)
    }, [report, layout, canViewAllUsers])

    const actionValues = useMemo(() => report.actions.map((action) => action.value), [report])
    // The table view has no per-chart scoping -- it is one combined breakdown
    // of the whole report -- so it still uses the report-level series: one
    // series per action.
    const series = useMemo(() => seriesFor(report), [report])

    // Scopes the "created"/"edited" tiles to the report's headline actions
    // (e.g. incidents excludes parties, party people, party vehicles and
    // documents) -- see `headlineActionsFor` and `buildKpis`.
    const headlineActions = useMemo(() => headlineActionsFor(report), [report])
    const kpis = useMemo(
        () => buildKpis(rows, startDate, endDate, actionMeta, headlineActions),
        [rows, startDate, endDate, actionMeta, headlineActions]
    )
    // Extra tiles a report declares beyond the five generic ones above (e.g.
    // incidents' "Documents uploaded") -- see `extraTilesFor` /
    // `report.extraTiles` in reportRegistry.js. Empty for any report that
    // doesn't declare one, so this adds nothing to the other reports' rows.
    const extraTiles = useMemo(() => extraTilesFor(report), [report])
    const extraTileValues = useMemo(
        () => extraTiles.map((tile) => buildExtraTileValue(rows, actionMeta, tile.actions)),
        [extraTiles, rows, actionMeta]
    )
    // Feeds the "Most active user" tile below, on both layouts -- computed
    // unconditionally (rather than only for the Users layout) to keep the
    // hooks order stable and this component free of a conditional Hook call.
    const userKpis = useMemo(
        () => buildUserKpis(rows, startDate, endDate, actionMeta, granularity),
        [rows, startDate, endDate, actionMeta, granularity]
    )
    const entityColor = useMemo(() => entityColorFor(report), [report])
    const leaderboard = useMemo(
        () => buildLeaderboard(rows, actionMeta, entityColor),
        [rows, actionMeta, entityColor]
    )

    // The scope layout's own leaderboard/KPI data -- keyed by `row.scopeNames`
    // (a board, or an inbox) rather than by person. The Hook calls themselves
    // stay unconditional, same reasoning as `userKpis` above: that is what
    // lets `layout` switch without upsetting hooks order. `scopeTabFor`
    // returns null for a report that never declares a `scopeTab` (every
    // report except boards and inbox), in which case there is nothing for
    // either builder to key by -- so each memo's *body* skips the work and
    // reads back one of the module-level empty constants above instead,
    // rather than running `buildLeaderboard`/`buildScopeKpis` over rows on
    // every other report only to have the result go unread.
    const scopeTab = useMemo(() => scopeTabFor(report), [report])
    const byScope = useCallback((row) => row.scopeNames, [])
    const scopeLeaderboard = useMemo(
        () =>
            scopeTab
                ? buildLeaderboard(rows, actionMeta, entityColor, { groupKeyFn: byScope, groupKey: 'scopeName' })
                : EMPTY_LEADERBOARD,
        [scopeTab, rows, actionMeta, entityColor, byScope]
    )
    const scopeKpis = useMemo(
        () => (scopeTab ? buildScopeKpis(rows, actionMeta, byScope) : EMPTY_SCOPE_KPIS),
        [scopeTab, rows, actionMeta, byScope]
    )

    // Each chart may scope itself to a subset of the report's actions (see
    // `chart.actions` in the registry) so it never has to mix incompatible
    // record types -- an incidents chart and a documents chart, say -- into
    // one legend. `seriesForChart` resolves that scope per chart; an unscoped
    // chart still covers every action in the report, via the same `seriesFor`
    // the table view uses. A `leaderboard` chart is keyed by entity, not
    // action, so it takes its series from `buildLeaderboard` instead.
    const chartData = useMemo(
        () =>
            charts.map((chart) => {
                // The three scope-layout chart types are keyed by
                // `row.scopeNames`, not by action -- `seriesForChart` knows
                // nothing about that dimension (it falls back to
                // `seriesFor(report)`, the wrong, action-keyed series), so
                // they are handled up front and never reach it.
                if (chart.type === 'scopeLeaderboard') {
                    return {
                        chart,
                        data: scopeLeaderboard.data,
                        isEmpty: !scopeLeaderboard.data.length,
                        series: scopeLeaderboard.series,
                    }
                }
                if (chart.type === 'scopeTrend') {
                    const { data, series: trendSeries } = buildScopeTrend(rows, startDate, endDate, actionMeta, {
                        groupKeyFn: byScope,
                        granularity,
                    })
                    // Per-cell, matching the sibling `trend` branch below --
                    // not `!trendSeries.length`, which only catches a range
                    // with no boards at all. A board present with activity
                    // outside this date range (every cell zero) must still
                    // read as empty, the same as the module/users trend does.
                    const isEmpty = data.every((record) => trendSeries.every((item) => !record[item.key]))
                    return { chart, data, isEmpty, series: trendSeries }
                }
                if (chart.type === 'scopeHeatmap') {
                    const data = buildHeatmap(rows, startDate, endDate, actionMeta, {
                        groupKeyFn: byScope,
                        groupKey: 'scopeName',
                        granularity,
                        // Same action order/labels/colours the module's own
                        // action-keyed charts use (see the `heatmap` branch
                        // below) -- a scope heatmap's popover still gets the
                        // per-action breakdown, just never the Investigate
                        // link (there is no one "actor" a board or inbox
                        // resolves to). See `ReportBody`'s render below,
                        // where `onInvestigate` is passed only to `heatmap`.
                        actions: actionValues,
                    })
                    return { chart, data, isEmpty: !data.grid.length, series: [] }
                }

                const chartActionValues = chart.actions || actionValues
                const chartSeries = chart.type === 'leaderboard' ? leaderboard.series : seriesForChart(report, chart)

                if (chart.type === 'trend') {
                    // A chart marked `colorBy: 'entity'` (the Users-view trend)
                    // needs its daily totals keyed by entity, not action, to
                    // match `chartSeries` above (see `seriesForChart`).
                    const seriesKeyFn =
                        chart.colorBy === 'entity' ? (row, meta) => meta?.entity : undefined
                    const data = buildActionTrend(
                        rows,
                        startDate,
                        endDate,
                        chartActionValues,
                        actionMeta,
                        seriesKeyFn,
                        granularity
                    )
                    // Every day is emitted regardless of activity (see
                    // `buildActionTrend`), so an empty range is never a shorter
                    // array -- it is a full array of zeroes.
                    const isEmpty = data.every((record) => chartSeries.every((item) => !record[item.key]))
                    return { chart, data, isEmpty, series: chartSeries }
                }
                if (chart.type === 'heatmap') {
                    // `actions: actionValues` orders/colours/labels each cell's
                    // `byAction` breakdown the same way `chartSeries` already
                    // does for this chart (both come from the report's own
                    // action list -- see `seriesForChart`'s fallback to
                    // `seriesFor(report)` for an unscoped chart like this
                    // one), so the popover agrees with the rest of the report
                    // about what a colour means.
                    const data = buildHeatmap(rows, startDate, endDate, actionMeta, { granularity, actions: actionValues })
                    return { chart, data, isEmpty: !data.grid.length, series: chartSeries }
                }
                if (chart.type === 'leaderboard') {
                    return { chart, data: leaderboard.data, isEmpty: !leaderboard.data.length, series: chartSeries }
                }
                const data = buildActionMix(rows, {
                    groupKeyFn: (row) => row[chart.groupBy],
                    actions: chartActionValues,
                    groupKey: chart.groupBy,
                    actionMeta,
                    // A `colorBy: 'direction'` chart stacks inbound vs outbound
                    // rather than created vs edited, so its segments must be
                    // keyed to match `chartSeries` above (see `seriesForChart`).
                    // `seriesKeys` is that same `chartSeries` list, narrowed to
                    // just the keys -- it is what lets `buildActionMix` drop a
                    // row whose `direction` is neither ('unknown', when the
                    // underlying message row was deleted after its history row
                    // was written) instead of counting it into `total` with no
                    // segment to show for it. See `buildActionMix`'s own
                    // docstring in activityReport.js.
                    ...(chart.colorBy === 'direction'
                        ? { seriesKeyFn: (row) => row.direction, seriesKeys: chartSeries.map((item) => item.key) }
                        : {}),
                })
                return { chart, data, isEmpty: !data.length, series: chartSeries }
            }),
        [charts, rows, startDate, endDate, actionValues, actionMeta, report, leaderboard, scopeLeaderboard, byScope, granularity]
    )

    if (!rows.length) {
        return <p className="reports-empty">No activity in the selected date range.</p>
    }

    const leaderboardTable = buildLeaderboardTable(leaderboard.data, leaderboard.series)
    // Not a Hook, so this can skip the work outright rather than reading back
    // an empty constant -- only the `scope` layout's table view ever reads
    // it (see the `view === 'table'` branch below), and `scopeLeaderboard`
    // is already `EMPTY_LEADERBOARD` on a report with no scope tab anyway.
    const scopeLeaderboardTable =
        layout === 'scope' ? buildLeaderboardTable(scopeLeaderboard.data, scopeLeaderboard.series, 'scopeName') : null

    // On the inbox report, fold the in/out breakdown into the "Messages
    // created" tile itself rather than showing it as a separate tile (see
    // `buildDirectionTotals` above). `created` and `in + out` are the same
    // two numbers by construction -- the headline is
    // `directionTotals.inbound + directionTotals.outbound`, not a second,
    // independent count that merely happens to agree with it. Both are
    // derived from the same already-filtered `rows` every other tile on this
    // row uses, so a row the Role filter does not admit (No Actor, by
    // default -- Reps is the only role selected out of the box) reads
    // "0 in" here exactly as it reads zero everywhere else on the page.
    const createdValue = report.hasDirection ? directionTotals.inbound + directionTotals.outbound : kpis.created
    const createdCaption = report.hasDirection
        ? `${directionTotals.inbound.toLocaleString()} in / ${directionTotals.outbound.toLocaleString()} out`
        : 'distinct records, not update events, in this range'

    return (
        <>
            {!canViewAllUsers && (
                <p className="reports-scope-banner">
                    <i className="fa fa-user" aria-hidden="true"></i>
                    Showing your own activity &mdash; you don&rsquo;t have permission to see other users on this report.
                </p>
            )}

            <StatTileRow>
                <StatTile
                    label={`${report.noun} created`}
                    value={createdValue}
                    testId="kpi-created"
                    caption={createdCaption}
                />
                <StatTile
                    label={`${report.noun} edited`}
                    value={kpis.edited}
                    testId="kpi-edited"
                    // Plain statement of what the number is -- the "unions ids
                    // across days and users" mechanics stay a code comment on
                    // the maths itself (see `buildKpis` in activityReport.js),
                    // not on-screen.
                    caption="counted once, no matter how many times it was edited"
                />
                {layout === 'scope' && scopeTab ? (
                    <>
                        <StatTile
                            label={`${scopeTab.plural} active`}
                            value={scopeKpis.activeCount}
                            testId="kpi-scope-active"
                            caption={`${scopeTab.plural.toLowerCase()} with any activity in this range`}
                        />
                        <StatTile
                            label={`Busiest ${scopeTab.label.toLowerCase()}`}
                            value={scopeKpis.topName}
                            testId="kpi-scope-top"
                            caption={`${scopeKpis.topCount.toLocaleString()} actions`}
                        />
                    </>
                ) : (
                    <>
                        <StatTile
                            label="Total edit events"
                            value={kpis.editEvents}
                            testId="kpi-edit-events"
                            caption="every update logged, across every record type in this report"
                        />
                        <StatTile
                            label="Active users"
                            value={kpis.activeUsers}
                            testId="kpi-active-users"
                            caption="distinct people or accounts admitted by the filters"
                        />
                    </>
                )}
                <StatTile
                    label="Most active user"
                    value={userKpis.topUser}
                    caption={`${userKpis.topUserCount.toLocaleString()} actions`}
                    testId="kpi-most-active-user"
                />
                {extraTiles.map((tile, index) => (
                    <StatTile
                        key={tile.key || tile.label}
                        label={tile.label}
                        value={extraTileValues[index]}
                        caption={tile.caption}
                        testId={tile.testId || `kpi-extra-${tile.key || index}`}
                    />
                ))}
            </StatTileRow>

            {view === 'table' ? (
                layout === 'scope' && scopeTab ? (
                    <DataTable
                        rows={scopeLeaderboardTable.rows}
                        series={scopeLeaderboardTable.columns}
                        rowHeader={scopeTab.label}
                    />
                ) : layout === 'users' ? (
                    <DataTable
                        rows={leaderboardTable.rows}
                        series={leaderboardTable.columns}
                        rowHeader="Staff member"
                    />
                ) : (
                    <DataTable
                        rows={buildMixTableRows(
                            buildActionMix(rows, {
                                groupKeyFn: (row) => row.userName,
                                actions: actionValues,
                                groupKey: 'userName',
                                actionMeta,
                            }),
                            series,
                            'userName'
                        )}
                        series={series}
                        rowHeader="Staff member"
                    />
                )
            ) : (
                <Row>
                    {chartData.map(({ chart, data, isEmpty, series: chartSeries }) => (
                        <Col xs="12" md={chart.type === 'actionMix' ? '6' : '12'} key={chart.title}>
                            <ChartCard
                                title={fillPeriod(chart.title, granularity)}
                                subtitle={fillPeriod(chart.caption, granularity)}
                                isEmpty={isEmpty}
                                emptyMessage="No activity in the selected date range."
                            >
                                {chart.type === 'trend' && <TrendLine data={data} series={chartSeries} />}
                                {chart.type === 'actionMix' && (
                                    <HorizontalStackedBar data={data} series={chartSeries} yKey={chart.groupBy} />
                                )}
                                {chart.type === 'heatmap' && <ActivityHeatmap {...data} onInvestigate={onInvestigate} />}
                                {chart.type === 'leaderboard' && <Leaderboard data={data} series={chartSeries} />}
                                {chart.type === 'scopeLeaderboard' && (
                                    <Leaderboard
                                        data={data}
                                        series={chartSeries}
                                        yKey="scopeName"
                                        unit={scopeTab.plural.toLowerCase()}
                                    />
                                )}
                                {chart.type === 'scopeTrend' && <TrendLine data={data} series={chartSeries} />}
                                {chart.type === 'scopeHeatmap' && (
                                    <ActivityHeatmap
                                        {...data}
                                        rowKey="scopeName"
                                        rowHeader={scopeTab.label}
                                        unit={scopeTab.plural.toLowerCase()}
                                    />
                                )}
                            </ChartCard>
                        </Col>
                    ))}
                </Row>
            )}
        </>
    )
}

export default ReportBody
